/**
 * The builder's Settings tab: the options that belong to the sharing setup as a
 * whole rather than to any one intent's form.
 *
 * So far that is the reshare story — how a reshared message tells the recipient
 * where it came from, passing along each sender's allowed detail. The detail is
 * the sender's answer to one answerable slot of the intro form, which the tab
 * names by the slot's stable field id; a prefix and suffix frame that answer as
 * a first person sentence. With no slot chosen the story carries no detail and
 * the settings have no `allowedDetailSettings` at all. The settings are part of
 * the topic's draft: edits go straight to `onChange`, and the edit form's save
 * stores them on the topic, along with whose detail the story carries and how
 * it describes the first sender. Those two shape the story whether or not it
 * includes the detail.
 */

import React, { useMemo, useState } from 'react';
import type { SharingFormSlot } from './types';
import {
  STORY_PARTICIPANT_CHOICES,
  DEFAULT_STORY_PARTICIPANTS,
  DEFAULT_STARTING_SENDER_APPOSITIVE,
  type AllowedDetailSettings,
  type StoryParticipant,
  type ReshareStorySettings,
} from '@/modules/topic/types';
import styles from './SharingSettingsPanel.module.css';

const STORY_PARTICIPANT_LABELS: Record<StoryParticipant, string> = {
  sender: 'who sent it to you',
  startingSender: 'who got the ball rolling',
  between: 'everyone in between',
};

const FIRST_SENDER_CHOICES = [
  'the author',
  DEFAULT_STARTING_SENDER_APPOSITIVE,
  'who started this game',
] as const;

/** One choice in a row of chips; `pressed` is what the author has picked. */
const Chip: React.FC<{
  label: string;
  pressed: boolean;
  onClick: () => void;
  testId: string;
  dashed?: boolean;
}> = ({ label, pressed, onClick, testId, dashed = false }) => (
  <button
    type="button"
    aria-pressed={pressed}
    className={`${styles.chip} ${dashed ? styles.chipDashed : ''}`}
    onClick={onClick}
    data-testid={testId}
  >
    {label}
  </button>
);

const SharingSettingsPanel: React.FC<{
  /** The intro form, whose answerable slots the detail can come from. */
  introSlots: readonly SharingFormSlot[];
  /** The topic's draft settings, or null when it has never saved this tab. */
  settings: ReshareStorySettings | null;
  /** Hand edited settings back to the draft. */
  onChange: (settings: ReshareStorySettings) => void;
  /** The appositive shown picked until the author picks one: the type's default. */
  defaultStartingSenderAppositive: string;
}> = ({ introSlots, settings, onChange, defaultStartingSenderAppositive }) => {
  // Never-saved settings read as no slot, with an empty sentence.
  const detail = settings?.allowedDetailSettings;
  const prefix = detail?.prefix ?? '';
  const slotId = detail?.slotId ?? '';
  const suffix = detail?.suffix ?? '';
  // Choosing "None" drops the allowed detail's settings, sentence and all.
  const chooseSlot = (chosen: string) => {
    const next: ReshareStorySettings = { ...settings };
    if (chosen) {
      next.allowedDetailSettings = {
        ...detail,
        prefix,
        suffix,
        slotId: chosen,
      };
    } else {
      delete next.allowedDetailSettings;
    }
    onChange(next);
  };
  // Only reachable once a slot is chosen: the prefix and suffix are disabled
  // until then.
  const updateDetail = (change: Partial<AllowedDetailSettings>) =>
    detail &&
    onChange({ ...settings, allowedDetailSettings: { ...detail, ...change } });
  // Whose detail the story carries and how it describes the first sender sit
  // beside the allowed detail's own settings, not inside them.
  const updateStory = (
    change: Omit<Partial<ReshareStorySettings>, 'allowedDetailSettings'>
  ) => onChange({ ...settings, ...change });

  // Each Select and Fill in slot is listed by its prompt verbatim (its editor
  // won't close without one), which tells apart slots the sender sees, and is
  // stored by its field id, which survives a reworded prompt. An optional slot
  // is only switched on or off, so it has no answer to pass along.
  const slotChoices = useMemo(() => {
    const choices = introSlots.flatMap(slot =>
      slot.type === 'select' || slot.type === 'fillIn'
        ? [{ id: slot.id, prompt: slot.prompt }]
        : []
    );
    // A saved slot the intro form no longer has stays listed, so the select
    // shows that the stored choice is gone rather than silently showing another.
    return slotId && !choices.some(c => c.id === slotId)
      ? [...choices, { id: slotId, prompt: 'Removed slot' }]
      : choices;
  }, [introSlots, slotId]);
  const noSlots = slotChoices.length === 0;

  const participants = settings?.participants ?? DEFAULT_STORY_PARTICIPANTS;
  // Stored in STORY_PARTICIPANT_CHOICES order, whatever order the chips were tapped in.
  const toggleParticipant = (value: StoryParticipant) =>
    updateStory({
      participants: STORY_PARTICIPANT_CHOICES.filter(c =>
        c === value ? !participants.includes(c) : participants.includes(c)
      ),
    });

  const appositive =
    settings?.startingSenderAppositive ?? defaultStartingSenderAppositive;
  const isPreset = (FIRST_SENDER_CHOICES as readonly string[]).includes(
    appositive
  );
  // "other…" stays picked while the author types, even through a value that
  // happens to match a preset; a stored phrase that isn't a preset opens on it.
  const [otherPicked, setOtherPicked] = useState(!isPreset);
  const otherPressed = otherPicked || !isPreset;

  const t = 'form-builder-settings';
  return (
    <div className={styles.panel} data-testid={t}>
      <div className={styles.sectionHeading}>Reshare story</div>
      <div className={styles.group}>
        <div className={styles.section}>
          <label className={styles.slotPicker}>
            Slot providing detail allowed to be passed along
            <select
              value={slotId}
              disabled={noSlots}
              onChange={e => chooseSlot(e.target.value)}
              className={`${styles.input} ${styles.select} ${styles.slotSelect}`}
              data-testid={`${t}-slot`}
            >
              <option value="">None</option>
              {slotChoices.map(choice => (
                <option key={choice.id} value={choice.id}>
                  {choice.prompt}
                </option>
              ))}
            </select>
          </label>
          {noSlots && (
            <p className={styles.example} data-testid={`${t}-no-slots`}>
              Add a Select or Fill in slot to the intro to choose one.
            </p>
          )}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>prefix</span>
            <input
              type="text"
              value={prefix}
              placeholder="optional"
              disabled={!detail}
              onChange={e => updateDetail({ prefix: e.target.value })}
              className={`${styles.input} ${styles.placeholderUntilFocus}`}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-testid={`${t}-prefix`}
            />
          </div>
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>suffix</span>
            <input
              type="text"
              value={suffix}
              placeholder="optional"
              disabled={!detail}
              onChange={e => updateDetail({ suffix: e.target.value })}
              className={`${styles.input} ${styles.placeholderUntilFocus}`}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-testid={`${t}-suffix`}
            />
          </div>
          <p className={styles.example} data-testid={`${t}-example`}>
            Selected form field should combine with prefix &amp; suffix to form
            a first person sentence, e.g. if form had options for “desert island
            items”, you could use prefix “I would bring a” and suffix “to the
            island” to form “I would bring a <strong>mirror</strong> to the
            island.”
          </p>
        </div>

        <div className={styles.section}>
          <span className={styles.rowTitle}>Participants in the story</span>
          <div className={styles.chips}>
            {STORY_PARTICIPANT_CHOICES.map(c => (
              <Chip
                key={c}
                label={STORY_PARTICIPANT_LABELS[c]}
                pressed={participants.includes(c)}
                onClick={() => toggleParticipant(c)}
                testId={`${t}-participant-${c}`}
              />
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <span className={styles.rowTitle}>
            Extra info about the first sender (appositive)
          </span>
          <div className={styles.chips}>
            {FIRST_SENDER_CHOICES.map((label, i) => (
              <Chip
                key={label}
                label={label}
                pressed={!otherPressed && appositive === label}
                onClick={() => {
                  setOtherPicked(false);
                  updateStory({ startingSenderAppositive: label });
                }}
                testId={`${t}-first-sender-${i}`}
              />
            ))}
            <Chip
              label="other…"
              pressed={otherPressed}
              onClick={() => {
                setOtherPicked(true);
                if (isPreset) updateStory({ startingSenderAppositive: '' });
              }}
              testId={`${t}-first-sender-other`}
              dashed
            />
          </div>
          {otherPressed && (
            <input
              type="text"
              value={appositive}
              placeholder="who… (leave empty for none)"
              onChange={e =>
                updateStory({ startingSenderAppositive: e.target.value })
              }
              className={styles.input}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-testid={`${t}-first-sender-other-input`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SharingSettingsPanel;
